import Contentstack from 'contentstack';

// Contentstack configuration
console.log('🔧 Initializing Contentstack SDK...');
console.log('🔑 API Key exists:', !!process.env.REACT_APP_CONTENTSTACK_API_KEY);
console.log('🎫 Delivery Token exists:', !!process.env.REACT_APP_CONTENTSTACK_DELIVERY_TOKEN);
console.log('🌍 Environment:', process.env.REACT_APP_CONTENTSTACK_ENVIRONMENT || 'development');

const Stack = Contentstack.Stack({
  'api_key': process.env.REACT_APP_CONTENTSTACK_API_KEY,
  'delivery_token': process.env.REACT_APP_CONTENTSTACK_DELIVERY_TOKEN,
  'environment': process.env.REACT_APP_CONTENTSTACK_ENVIRONMENT || 'development'
});

console.log('✅ Contentstack Stack initialized:', Stack);

// Management API configuration for creating entries
const MANAGEMENT_API_BASE_URL = 'https://api.contentstack.io/v3';
const MANAGEMENT_TOKEN = process.env.REACT_APP_CONTENTSTACK_MANAGEMENT_TOKEN;
const API_KEY = process.env.REACT_APP_CONTENTSTACK_API_KEY;

class ContentstackService {
  // Fetch landing page content
  async getLandingPageContent() {
    try {
      const Query = Stack.ContentType('landing_page').Query();
      const result = await Query.toJSON().find();
      return result[0]?.[0] || null;
    } catch (error) {
      console.error('Error fetching landing page content:', error);
      throw error;
    }
  }

  // Create a new subscriber entry
  async createSubscriber(subscriberData) {
    try {
      const response = await fetch(`${MANAGEMENT_API_BASE_URL}/content_types/subscribers/entries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api_key': API_KEY,
          'authorization': MANAGEMENT_TOKEN,
        },
        body: JSON.stringify({
          entry: {
            title: subscriberData.name,
            email: subscriberData.email,
            locale: 'en-us'
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Publish the entry to make it available via Delivery API
      try {
        await this.publishEntry('subscribers', result.entry.uid);
        console.log('Subscriber published successfully');
      } catch (publishError) {
        console.error('Failed to publish subscriber, but entry was created:', publishError);
        // Don't throw here - the entry was created successfully
      }
      
      return result.entry;
    } catch (error) {
      console.error('Error creating subscriber:', error);
      throw error;
    }
  }

  // Create a new resume entry
  async createResume(resumeData) {
    try {
      // Transform the resume data to match Contentstack schema
      const transformedData = this.transformResumeData(resumeData, false); // Pass isUpdate = false for new resumes
      console.log('Transformed data for Contentstack:', transformedData);

      const requestBody = {
        entry: {
          ...transformedData,
          locale: 'en-us'
        }
      };
      console.log('Request body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(`${MANAGEMENT_API_BASE_URL}/content_types/resume_data/entries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api_key': API_KEY,
          'authorization': MANAGEMENT_TOKEN,
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        console.error('Response status:', response.status);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const result = await response.json();
      console.log('Success response:', result);
      
      // Publish the entry to make it available via Delivery API
      try {
        await this.publishEntry('resume_data', result.entry.uid);
        console.log('Resume published successfully');
        // Add a flag to indicate successful publishing
        result.entry._published = true;
      } catch (publishError) {
        console.error('Failed to publish resume, but entry was created:', publishError);
        // Parse publishing error for better feedback
        let publishErrorMessage = 'Failed to publish entry';
        try {
          if (publishError.message.includes('{')) {
            const errorStart = publishError.message.indexOf('{');
            const errorJson = publishError.message.substring(errorStart);
            const parsedError = JSON.parse(errorJson);
            if (parsedError.error_message) {
              publishErrorMessage = parsedError.error_message;
            }
            if (parsedError.errors) {
              const fieldErrors = Object.keys(parsedError.errors).map(field => {
                const fieldName = field.replace(/_/g, ' ').replace(/\./g, ' → ');
                const errorDetails = Array.isArray(parsedError.errors[field]) 
                  ? parsedError.errors[field].join(', ')
                  : parsedError.errors[field];
                return `${fieldName}: ${errorDetails}`;
              });
              publishErrorMessage = `Publishing failed - ${fieldErrors.join('; ')}`;
            }
          }
        } catch (parseError) {
          console.error('Error parsing publish error:', parseError);
        }
        
        // Add publishing error info to result
        result.entry._publishError = publishErrorMessage;
        result.entry._published = false;
      }
      
      return result.entry;
    } catch (error) {
      console.error('Error creating resume:', error);
      throw error;
    }
  }

  // Update an existing resume entry
  async updateResume(entryUid, resumeData) {
    try {
      const transformedData = this.transformResumeData(resumeData, true);

      const response = await fetch(`${MANAGEMENT_API_BASE_URL}/content_types/resume_data/entries/${entryUid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'api_key': API_KEY,
          'authorization': MANAGEMENT_TOKEN,
        },
        body: JSON.stringify({
          entry: {
            ...transformedData,
            locale: 'en-us'
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Publish the entry to make it available via Delivery API
      try {
        await this.publishEntry('resume_data', result.entry.uid);
        console.log('Resume updated and published successfully');
        // Add a flag to indicate successful publishing
        result.entry._published = true;
      } catch (publishError) {
        console.error('Failed to publish updated resume, but entry was updated:', publishError);
        // Parse publishing error for better feedback
        let publishErrorMessage = 'Failed to publish updated entry';
        try {
          if (publishError.message.includes('{')) {
            const errorStart = publishError.message.indexOf('{');
            const errorJson = publishError.message.substring(errorStart);
            const parsedError = JSON.parse(errorJson);
            if (parsedError.error_message) {
              publishErrorMessage = parsedError.error_message;
            }
            if (parsedError.errors) {
              const fieldErrors = Object.keys(parsedError.errors).map(field => {
                const fieldName = field.replace(/_/g, ' ').replace(/\./g, ' → ');
                const errorDetails = Array.isArray(parsedError.errors[field]) 
                  ? parsedError.errors[field].join(', ')
                  : parsedError.errors[field];
                return `${fieldName}: ${errorDetails}`;
              });
              publishErrorMessage = `Publishing failed - ${fieldErrors.join('; ')}`;
            }
          }
        } catch (parseError) {
          console.error('Error parsing publish error:', parseError);
        }
        
        // Add publishing error info to result
        result.entry._publishError = publishErrorMessage;
        result.entry._published = false;
      }
      
      return result.entry;
    } catch (error) {
      console.error('Error updating resume:', error);
      throw error;
    }
  }

  // Transform resume data to match Contentstack schema
  transformResumeData(resumeData, isUpdate = false) {
    console.log('🔄 Starting transformResumeData with:', resumeData);
    
    // Base data object without title
    const baseData = {
      professional_summary: resumeData.professionalSummary || '',
      // profile_picture: resumeData.personal.profilePicture, // Handle file upload separately
      contact_details: {
        phone_number: this.extractPhoneNumber(resumeData.personal.phone),
        email_address: resumeData.personal.email || '',
        location: resumeData.personal.location || '',
        github_profile: resumeData.personal.github || '',
        linkedin_profile: resumeData.personal.linkedin || ''
      },
      education: {
        highest_qualification: resumeData.education.highestQualification || '',
        institution_name: resumeData.education.institutionName || '',
        start_date: this.convertToISODate(resumeData.education.startDate),
        end_date: this.convertToISODate(resumeData.education.endDate)
      },
      work_history: resumeData.workHistory.map((work, index) => {
        console.log(`🏢 Transforming work item ${index + 1}:`, work);
        // Use company_1 for all entries since all block types reference the same global field
        const item = {
          company_1: {
            employer: work.companyName || '',
            job_title: work.jobTitle || '',
            start_date: this.convertToISODate(work.startDate),
            end_date: work.present ? null : this.convertToISODate(work.endDate),
            present: work.present || false,
            job_responsibilities: work.responsibilities || '',
            employment_type: work.employmentType || 'Full Time'
          }
        };
        console.log(`✅ Transformed work to:`, item);
        return item;
      }),
      // Internships - field uid is "global_field" (not "internships")
      // Schema fields: role, organisation, number_of_months, responsibilities, add_certificate
      global_field: {
        role: resumeData.internships[0]?.role || '',
        organisation: resumeData.internships[0]?.company || '',
        number_of_months: parseInt(resumeData.internships[0]?.durationInMonths) || 0,
        responsibilities: resumeData.internships[0]?.responsibilities || ''
        // add_certificate: Handle file upload separately
      },
      // Skills stored in skills JSON field
      // Send as simple objects with skill and rating fields
      skills: (() => {
        console.log('🎨 Processing skills for skills field...');
        console.log('📝 Raw skills data:', resumeData.skills);
        
        const skillsData = [];
        
        // Add each skill as a simple object (skip empty ones)
        if (resumeData.skills && Array.isArray(resumeData.skills)) {
          resumeData.skills.forEach(skill => {
            const skillName = typeof skill === 'string' ? skill : (skill.name || '');
            const rating = typeof skill === 'object' ? (skill.rating || 0) : 0;
            // Only add if skill name is not empty
            if (skillName.trim()) {
              const skillObj = {
                skill: skillName,
                rating: rating
              };
              skillsData.push(skillObj);
              console.log('➕ Added skill:', skillObj);
            } else {
              console.log('⏭️ Skipped empty skill');
            }
          });
        }
        
        console.log('✅ Final skills field data (filtered):', JSON.stringify(skillsData, null, 2));
        console.log(`📊 Total skills: ${skillsData.length}`);
        return skillsData;
      })()
    };
    
    console.log('✅ Base data transformed:', baseData);

    // Add title only for new entries, not for updates
    if (!isUpdate) {
      const now = new Date();
      const dateStr = now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
      const timeStr = now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      const baseName = resumeData.personal.name || 'Untitled Resume';
      const uniqueTitle = `${baseName} - ${dateStr} ${timeStr}`;
      
      return {
        title: uniqueTitle,
        ...baseData
      };
    }

    // For updates, return data without title
    return baseData;
  }

  // Helper method to convert month input (YYYY-MM) to ISO date
  convertToISODate(monthString) {
    if (!monthString) return null;
    
    try {
      // Month input format is "YYYY-MM", convert to first day of month
      const [year, month] = monthString.split('-');
      if (year && month) {
        // Create date object for first day of the month
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return date.toISOString();
      }
    } catch (error) {
      console.error('Error converting date:', error);
    }
    
    return null;
  }

  // Helper method to extract 10-digit phone number
  extractPhoneNumber(phoneString) {
    if (!phoneString) return null;
    
    // Remove all non-digit characters
    const digits = phoneString.replace(/\D/g, '');
    
    // For exactly 10 digits, return as is
    if (digits.length === 10) {
      return parseInt(digits);
    }
    
    // For more than 10 digits, extract last 10 (for international numbers)
    if (digits.length > 10) {
      const last10Digits = digits.slice(-10);
      return parseInt(last10Digits);
    }
    
    // For less than 10 digits, pad with zeros at the beginning (if needed)
    if (digits.length > 0 && digits.length < 10) {
      console.warn(`Phone number has ${digits.length} digits, expected 10. Value: ${digits}`);
      return null; // Don't pad, return null for invalid numbers
    }
    
    return null;
  }

  // Retrieve resumes for a specific user (by email)
  async getUserResumes(userEmail) {
    try {
      console.log('🔍 getUserResumes called with email:', userEmail);
      console.log('🏗️ Creating Contentstack query...');
      
      const Query = Stack.ContentType('resume_data').Query();
      console.log('📝 Adding where clause for email:', userEmail);
      Query.where('contact_details.email_address', userEmail);
      console.log('📅 Adding descending order by updated_at');
      Query.descending('updated_at');
      
      console.log('🚀 Executing Contentstack query...');
      const result = await Query.toJSON().find();
      console.log('📦 Raw Contentstack query result:', result);
      console.log('📊 Result type:', typeof result);
      console.log('📏 Result length:', result?.length);
      
      // Contentstack returns [entries, count, schema] - we want the entries
      const entries = result[0] || [];
      console.log('📋 Extracted entries:', entries);
      console.log('🔢 Number of entries found:', entries.length);
      
      if (entries.length > 0) {
        console.log('✅ Found entries for user:', userEmail);
        console.log('📄 First entry preview:', entries[0]);
      } else {
        console.log('❌ No entries found for user:', userEmail);
      }
      
      return entries;
    } catch (error) {
      console.error('💥 Error in getUserResumes:', error);
      console.error('🔍 Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      throw error;
    }
  }

  // Get the latest resume for a user
  async getLatestUserResume(userEmail) {
    try {
      const resumes = await this.getUserResumes(userEmail);
      console.log('Retrieved resumes for user:', resumes);
      const latestResume = resumes.length > 0 ? resumes[0] : null;
      console.log('Latest resume:', latestResume);
      return latestResume;
    } catch (error) {
      console.error('Error fetching latest resume:', error);
      return null;
    }
  }

  // Test method to fetch all resumes (for debugging)
  async getAllResumes() {
    try {
      console.log('Fetching all resumes for debugging...');
      const Query = Stack.ContentType('resume_data').Query();
      Query.descending('updated_at');
      const result = await Query.toJSON().find();
      console.log('All resumes result:', result);
      const entries = result[0] || [];
      console.log('All resume entries:', entries);
      return entries;
    } catch (error) {
      console.error('Error fetching all resumes:', error);
      throw error;
    }
  }

  // Transform Contentstack data back to app format
  transformFromContentstack(contentstackData) {
    console.log('🔄 transformFromContentstack called');
    console.log('📥 Raw Contentstack data:', contentstackData);
    
    if (!contentstackData) {
      console.log('⚠️ No Contentstack data provided');
      return null;
    }

    console.log('🏢 work_history field:', contentstackData.work_history);
    console.log('👥 global_field (internships):', contentstackData.global_field);
    console.log('🔧 skills field:', contentstackData.skills);
    
    // Extract skills from skills JSON field
    let skills = [];
    
    console.log('🎨 Extracting from skills field...');
    console.log('📦 skills field type:', typeof contentstackData.skills);
    console.log('📦 skills field is array:', Array.isArray(contentstackData.skills));
    console.log('📦 skills field content:', JSON.stringify(contentstackData.skills, null, 2));
    
    if (contentstackData.skills && Array.isArray(contentstackData.skills)) {
      contentstackData.skills.forEach((item, index) => {
        console.log(`🔍 Processing skills item ${index}:`, item);
        console.log(`   Type: ${typeof item}`, item);
        
        // Check if it's a simple object with skill and rating fields (NEW FORMAT)
        if (typeof item === 'object' && item.skill) {
          console.log('✅ Found simple skill object:', item);
          skills.push({
            name: item.skill || '',
            rating: item.rating || 0
          });
        }
        // Check if it's a string (fallback format: "React (4/5)")
        else if (typeof item === 'string') {
          console.log('✅ Found skill string:', item);
          // Parse the string format "SkillName (Rating/5)"
          const match = item.match(/^(.+?)\s*\((\d+)\/5\)$/);
          if (match) {
            skills.push({
              name: match[1].trim(),
              rating: parseInt(match[2])
            });
          } else {
            // Fallback if format doesn't match - add with default rating
            skills.push({
              name: item,
              rating: 0
            });
          }
        }
        // Check if it's an object with _content_type (old format)
        else if (typeof item === 'object' && item._content_type === 'skill') {
          console.log('✅ Found old skill object with _content_type:', item);
          skills.push({
            name: item.skill_name || '',
            rating: item.rating || 0
          });
        }
        // Fallback for even older structure (type/data)
        else if (typeof item === 'object' && item.type === 'skills' && item.data) {
          console.log('⚠️ Found OLD skills structure:', item.data);
          skills = item.data.map(skillData => ({
            name: skillData.skill || skillData.name || '',
            rating: skillData.rating || 0
          }));
        }
        else {
          console.log('⚠️ Unknown skill format:', item);
        }
      });
    } else {
      console.log('⚠️ Skills field is not an array or is null');
    }
    
    console.log('📋 Final skills array:', skills);
    
    // Transform internships from global_field
    const internships = [];
    if (contentstackData.global_field) {
      internships.push({
        id: Date.now() + Math.random(),
        role: contentstackData.global_field.role || '',
        company: contentstackData.global_field.organisation || '',
        durationInMonths: contentstackData.global_field.number_of_months?.toString() || '',
        responsibilities: contentstackData.global_field.responsibilities || '',
        certificate: null
      });
    } else {
      // Default empty internship for UI
      internships.push({
        id: 1,
        role: '',
        company: '',
        durationInMonths: '',
        responsibilities: '',
        certificate: null
      });
    }
    
    const transformed = {
      personal: {
        name: contentstackData.title?.split(' - ')[0] || '',
        email: contentstackData.contact_details?.email_address || '',
        phone: contentstackData.contact_details?.phone_number?.toString() || '',
        location: contentstackData.contact_details?.location || '',
        linkedin: contentstackData.contact_details?.linkedin_profile || '',
        github: contentstackData.contact_details?.github_profile || '',
        website: '',
        profilePicture: contentstackData.profile_picture?.url || null
      },
      professionalSummary: contentstackData.professional_summary || '',
      education: {
        highestQualification: contentstackData.education?.highest_qualification || '',
        institutionName: contentstackData.education?.institution_name || '',
        startDate: this.convertFromISODate(contentstackData.education?.start_date),
        endDate: this.convertFromISODate(contentstackData.education?.end_date)
      },
      workHistory: this.transformWorkHistory(contentstackData.work_history || []),
      internships: internships,
      projects: [{
        id: 1,
        projectName: '',
        projectLink: '',
        description: ''
      }],  // Projects not in Contentstack schema - always return empty default
      skills: skills
    };
    
    console.log('✅ Transformed data:', transformed);
    return transformed;
  }

  // Helper to convert ISO date back to month format
  convertFromISODate(isoString) {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      return `${year}-${month}`;
    } catch (error) {
      console.error('Error converting ISO date:', error);
      return '';
    }
  }

  // Transform work history from Contentstack format
  transformWorkHistory(workHistoryArray) {
    console.log('🔄 transformWorkHistory called with:', workHistoryArray);
    console.log('📊 Is Array:', Array.isArray(workHistoryArray), 'Length:', workHistoryArray?.length);
    
    if (!workHistoryArray || workHistoryArray.length === 0) {
      console.log('⚠️ Work history array is empty or undefined');
      return [{
        id: 1,
        companyName: '',
        jobTitle: '',
        employmentType: 'Full Time',
        startDate: '',
        endDate: '',
        present: false,
        responsibilities: ''
      }];
    }
    
    const workHistory = [];
    
    workHistoryArray.forEach((workItem, index) => {
      console.log(`📝 Processing work item ${index}:`, workItem);
      console.log(`🔑 Keys in work item:`, Object.keys(workItem));
      
      Object.keys(workItem).forEach(key => {
        if (key.startsWith('company_')) {
          const company = workItem[key];
          console.log(`✅ Found company data for ${key}:`, company);
          workHistory.push({
            id: Date.now() + Math.random(),
            companyName: company.employer || '',
            jobTitle: company.job_title || '',
            employmentType: company.employment_type || 'Full Time',
            startDate: this.convertFromISODate(company.start_date),
            endDate: company.present ? '' : this.convertFromISODate(company.end_date),
            present: company.present || false,
            responsibilities: company.job_responsibilities || ''
          });
        }
      });
    });
    
    console.log('📦 Final transformed work history:', workHistory);
    
    return workHistory.length > 0 ? workHistory : [{
      id: 1,
      companyName: '',
      jobTitle: '',
      employmentType: 'Full Time',
      startDate: '',
      endDate: '',
      present: false,
      responsibilities: ''
    }];
  }

  // Transform projects from Contentstack format
  transformProjects(projectsArray) {
    const projects = [];
    
    projectsArray.forEach(projectItem => {
      Object.keys(projectItem).forEach(key => {
        if (key.startsWith('project_')) {
          const project = projectItem[key];
          projects.push({
            id: Date.now() + Math.random(),
            projectName: project.project_name || '',
            projectLink: project.project_link || '',
            description: project.description || ''
          });
        }
      });
    });
    
    return projects.length > 0 ? projects : [{
      id: 1,
      projectName: '',
      projectLink: '',
      description: ''
    }];
  }

  // Publish an entry to make it available via Delivery API
  async publishEntry(contentTypeUid, entryUid) {
    try {
      console.log(`Publishing entry ${entryUid} in content type ${contentTypeUid}`);
      
      const environment = process.env.REACT_APP_CONTENTSTACK_ENVIRONMENT || 'development';
      const url = `${MANAGEMENT_API_BASE_URL}/content_types/${contentTypeUid}/entries/${entryUid}/publish`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': MANAGEMENT_TOKEN,
          'api_key': API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          entry: {
            environments: [environment],
            locales: ['en-us']
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Publish response error:', errorData);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorData}`);
      }

      const result = await response.json();
      console.log('Entry published successfully:', result);
      return result;
    } catch (error) {
      console.error('Error publishing entry:', error);
      throw error;
    }
  }

  // Upload file to Contentstack
  async uploadFile(file) {
    try {
      const formData = new FormData();
      formData.append('asset[upload]', file);
      formData.append('asset[title]', file.name);

      const response = await fetch(`${MANAGEMENT_API_BASE_URL}/assets`, {
        method: 'POST',
        headers: {
          'api_key': API_KEY,
          'authorization': MANAGEMENT_TOKEN,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.asset;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }
}

export default new ContentstackService();
